import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Input, notification, Typography } from 'antd';
import {
    ArrowRightOutlined, SafetyCertificateOutlined,
    LockOutlined, SecurityScanOutlined, UserOutlined,
} from '@ant-design/icons';
import { useAdminAuth } from '../../context/AdminAuthContext';
import adminApi from '../../api/adminAxios';
import SEO from '../../components/SEO';
import '../../admin.css';

const { Title, Text } = Typography;

const securityItems = [
    { icon: <LockOutlined />, title: 'End-to-End Encryption', desc: 'Your session is encrypted and protected at every step' },
    { icon: <SecurityScanOutlined />, title: 'Secure Verification', desc: 'Two-factor authentication keeps the admin portal safe' },
    { icon: <SafetyCertificateOutlined />, title: 'One-Time Code', desc: 'Your OTP expires in 5 minutes for your protection' },
];

export default function AdminMfa() {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useAdminAuth();

    const { mfa_token, mfa_type } = location.state || {};

    // Redirect to login if arrived here without a valid MFA token
    useEffect(() => {
        if (!mfa_token) {
            navigate('/admin/login', { replace: true });
        }
    }, [mfa_token, navigate]);

    const mfaDescription =
        mfa_type === 'email'         ? 'We sent a 6-digit code to your registered email address.' :
        mfa_type === 'sms'           ? 'We sent a 6-digit code to your registered mobile number.' :
        mfa_type === 'authenticator' ? 'Enter the 6-digit code from your authenticator app.' :
        'Enter the 6-digit verification code.';

    const handleSubmit = async () => {
        if (otp.length !== 6) {
            notification.error({ message: 'Invalid OTP', description: 'Please enter a valid 6-digit verification code.' });
            return;
        }

        setLoading(true);
        try {
            const res = await adminApi.post('/admin/verify-mfa', { mfa_token, otp });

            if (res.status === 200 && res.data.status === 'success') {
                login(res.data.data.admin);
                notification.success({
                    message: 'Login Successful',
                    description: `Welcome back, ${res.data.data.admin.name}!`,
                });
                navigate('/admin/dashboard', { replace: true });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Verification failed. Please try again.';
            notification.error({ message: 'Verification Failed', description: msg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-login-root">
            <SEO noindex={true} />

            {/* Left panel */}
            <div className="admin-login-left">
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 320 }}>
                    <div style={{
                        width: 64, height: 64, background: 'rgba(255,255,255,0.15)',
                        borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 24px', fontSize: 28, color: '#FCD34D',
                    }}>
                        <UserOutlined />
                    </div>
                    <Title level={2} style={{ color: '#fff', fontWeight: 800, margin: '0 0 8px' }}>
                        One Last Step
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, display: 'block', marginBottom: 36 }}>
                        Verify your identity to access the Oottan Admin Portal.
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {securityItems.map((item) => (
                            <div key={item.title} style={{
                                display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left',
                                background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 16px',
                            }}>
                                <span style={{ color: '#FCD34D', fontSize: 16, marginTop: 2 }}>{item.icon}</span>
                                <div>
                                    <Text style={{ color: '#fff', fontWeight: 600, display: 'block', fontSize: 14 }}>{item.title}</Text>
                                    <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>{item.desc}</Text>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div className="admin-login-right">
                <div className="admin-login-form-box">
                    <div style={{ marginBottom: 32, textAlign: 'center' }}>
                        <div style={{
                            width: 56, height: 56, background: 'var(--gradient-primary)',
                            borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px', fontSize: 24, color: '#fff',
                            boxShadow: '0 8px 24px rgba(173,20,87,0.3)',
                        }}>
                            <SafetyCertificateOutlined />
                        </div>
                        <Title level={3} style={{ fontWeight: 800, color: 'var(--color-text-primary)', margin: '0 0 6px' }}>
                            Verify Your Identity
                        </Title>
                        <Text style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
                            {mfaDescription}
                        </Text>
                    </div>

                    <Form layout="vertical" onFinish={handleSubmit}>
                        <Form.Item>
                            <Input.OTP
                                value={otp}
                                onChange={setOtp}
                                length={6}
                                autoFocus
                                formatter={(str) => str.toUpperCase()}
                                style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={loading}
                                icon={<ArrowRightOutlined />}
                                style={{
                                    height: 48, fontWeight: 700, fontSize: 15,
                                    background: 'var(--gradient-primary)',
                                    border: 'none', borderRadius: 'var(--radius-md)',
                                }}
                            >
                                Verify & Sign In
                            </Button>
                        </Form.Item>
                    </Form>

                </div>
            </div>
        </div>
    );
}
